import {Button, Typography} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import Modal from 'Shared/components/Modal';
import {useDropzone} from 'react-dropzone';
import './ClientOnboardingMaintenanceModal.sass';
import {uploadEmployerProfiles} from 'Enrollment/ducks/enrollmentFileProcessing';
import * as O from 'fp-ts/lib/Option';
import {useSelector, useDispatch} from 'react-redux';
import {pipe} from 'fp-ts/lib/pipeable';
import {createSnackbar} from 'Core/ducks/snackbar';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import {Scope} from 'Shared/constants/Scope';

const MAX_SIZE = 29360128; // 28 MB

const hasAbilityToUploadFile = (scopes: Scope[]) =>
  scopes.some(scope => /enrolltrack.employerprofile.upload/.test(scope));

export type FileUploadErrors = {
  selectedFormError: string | boolean;
  fileError: string | boolean;
};

const uploadEnrollmentEmployerProfiles = (file: File) => uploadEmployerProfiles(file);

export type EnrollmentEmployerProfileModalProps = {
  onClose: () => void;
};
const ClientOnboardingMaintenanceModal = (props: EnrollmentEmployerProfileModalProps) => {
  const {onClose} = props;
  const [file, setFile] = useState<File | undefined>();
  const [errors, setErrors] = useState<FileUploadErrors>({selectedFormError: false, fileError: false});
  const scopes = useSelector(selectUserScopes);
  const dispatch = useDispatch();

  const canUpload = hasAbilityToUploadFile(scopes);

  useEffect(() => {
    if (canUpload) {
      setFile(undefined);
    }
  }, [dispatch, canUpload]);

  const uploadFile = () => {
    pipe(uploadEnrollmentEmployerProfiles, O.some, O.ap(O.fromNullable(file)), O.map(dispatch));
  };

  const onDropAccepted = (acceptedFiles: File[]) => {
    setFile(acceptedFiles[0]);
    setErrors({
      ...errors,
      fileError: false,
    });
  };

  const onDropRejected = (acceptedFiles: File[]) => {
    setFile(undefined);
    setErrors({
      ...errors,
      fileError:
        acceptedFiles[0].size > MAX_SIZE
          ? `File Exceeds Maximum Allowed Size of ${MAX_SIZE}`
          : 'File Format Not Accepted.',
    });
    acceptedFiles[0].size > MAX_SIZE
      ? dispatch(createSnackbar(`File Exceeds Maximum Allowed Size of ${MAX_SIZE}`))
      : dispatch(createSnackbar('File Format Not Accepted.'));
  };

  const {getInputProps, getRootProps} = useDropzone({
    onDropAccepted,
    onDropRejected,
    accept: '.xls,.xlsx',
    maxSize: MAX_SIZE,
  });

  const handleUploadEnrollmentFile = () => {
    uploadFile();
    onClose();
  };

  const disableUploadButton = (): boolean | undefined => {
    let disabled = false;
    if (!file) {
      disabled = true;
    }
    return disabled;
  };

  return (
    <Modal open className="enrollment-file-upload-modal" onClose={onClose} title="Upload Files" disableBackdropClick>
      <div
        {...getRootProps({
          className: 'enrollment-file-upload-modal__file-input disabled',
        })}
      >
        <input {...getInputProps()} type="file" multiple hidden />
        <Typography
          className="enrollment-file-upload-modal__content"
          variant="body2"
          color={!!errors.fileError ? 'error' : 'initial'}
        >
          {(file && file.name) ||
            errors.fileError ||
            'Click or Drop a file within this container to upload a new file.'}
        </Typography>
      </div>
      <div className="modal__footer modal__footer-file-upload">
        <Button
          id="enrollment-file-upload-modal-close-button"
          className="modal__footer-fileupload-actions"
          variant="contained"
          onClick={onClose}
        >
          Close
        </Button>
        <Button
          id="enrollment-file-upload-modal-upload-button"
          className="modal__footer-file-upload-actions"
          color="primary"
          variant="contained"
          disabled={disableUploadButton()}
          onClick={handleUploadEnrollmentFile}
        >
          Upload
        </Button>
      </div>
    </Modal>
  );
};

export default ClientOnboardingMaintenanceModal;
